import { isNil } from "@clipboard-health/util-ts";
import { useWorkerCustomToBaseQualificationNameMap } from "@src/appV2/Qualifications/useWorkerCustomToBaseQualificationNameMap";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { useListViewOpenShiftsData } from "../../ListView/useListViewOpenShiftsData";
import { filterOpenShifts } from "../filterOpenShifts";
import { useShiftDiscoveryUserFiltersContext } from "../useUserFiltersContext";
import type { FiltersFormValues } from "./types";

export function useFilteredOpenShiftsStates(formValues: FiltersFormValues) {
  const { dates } = useShiftDiscoveryUserFiltersContext();
  const worker = useDefinedWorker();
  const workerHomeState = worker.address?.state;

  const { distance, includeWindowedOutShifts, includedStates, ...localUserFilters } = formValues;
  void includedStates; // intentionally omitted from localUserFilters

  const {
    shifts = [],
    workplacesMap,
    isLoading,
  } = useListViewOpenShiftsData({
    distance,
    includeWindowedOutShifts,
  });

  const customToBaseQualificationNameMap = useWorkerCustomToBaseQualificationNameMap();

  // need to get all shifts without the included states filter
  const filteredOpenShiftsData = filterOpenShifts(
    { shifts, workplacesMap },
    { dates, ...localUserFilters },
    customToBaseQualificationNameMap
  );

  const statesFromShifts = filteredOpenShiftsData.reduce((states, shift) => {
    if (shift.type !== "open-shift") {
      return states;
    }

    const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
    if (isNil(workplace?.attributes.location.state)) {
      return states;
    }

    states.add(workplace.attributes.location.state);
    return states;
  }, new Set<string>());

  // Check if any shifts exist in states other than the worker's home state
  const hasShiftsInNonHomeStates = [...statesFromShifts].some((state) => state !== workerHomeState);

  // Include worker's home state in available options when filter should be shown
  const states = new Set(statesFromShifts);
  if (workerHomeState && hasShiftsInNonHomeStates) {
    states.add(workerHomeState);
  }

  return {
    states,
    hasShiftsInNonHomeStates,
    isLoading,
  };
}
